/* Onboarding: the prototype's thirteen steps, wired to the API.

   The wizard itself is `prototype.jsx` and is the prototype's own code. This file is the
   whole of the difference between a design and a product.

   Three things happen here that the design could not know about.

   The organisation is created when the user leaves the org step, not at Finish. It has to
   exist by then, because installing the GitHub App is scoped to one -- the install link is
   `/organizations/{id}/github/install` -- so until there is an organisation there is
   nothing to install into. Finish then reconfigures that organisation rather than
   creating a second.

   Installing leaves the page. GitHub owns the repository picker, and it hands the browser
   back to the API's setup URL, which returns it here. A wizard holding its state in React
   alone would restart from step one on the way back, so the answers and the step live in
   sessionStorage across that round trip.

   Leaving for longer -- signing out, closing the app -- is covered by the account: every
   step is saved to it, and the wizard resumes from whichever copy was written last.

   Repositories are polled while any of them is still indexing. Indexing is minutes of work
   on another machine, so the wizard reports it rather than waiting for it: the user can
   finish and reach the dashboard with indexing still running. */

import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { api } from "@/api";
import { announceIndexRequested } from "@/notices";
import { OnboardingApp } from "@/onboarding/prototype";
import { useSession } from "@/session";

const DOCS_URL = "https://codenerve.ai/docs";
/* sessionStorage, not local: a draft is one tab's unfinished business, and a second tab
   resuming half of it would be worse than starting over. */
const DRAFT_KEY = "codenerve.onboarding";
const POLL_MS = 4000;
const HANDLE_CHECK_MS = 400;

function readDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/* The account's copy is saved on every step, the tab's only on the way out to GitHub --
   the one moment the tab's is the fresher of the two. */
function newestDraft(...drafts) {
  const usable = drafts.filter((draft) => Number.isInteger(draft?.step) && draft.answers);
  const savedAt = (draft) => Date.parse(draft.savedAt) || 0;
  return usable.sort((a, b) => savedAt(b) - savedAt(a))[0] ?? null;
}

export function Onboarding() {
  const { user, profile, organization, organizations, refresh, signOut } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [repositories, setRepositories] = useState(null);
  const [indexingCounts, setIndexingCounts] = useState(null);
  const [stats, setStats] = useState({ ruleFiles: 0 });
  const [handleError, setHandleError] = useState("");
  const handleCheckTimer = useRef(null);
  const actionPending = useRef(false);
  const createdOrg = useRef(null);
  const saved = useRef(false);
  /* Progress saves go one at a time, so a slow request cannot land after a later one and
     rewind the account to an earlier step. */
  const progressQueue = useRef(Promise.resolve());

  /* Re-running the wizard reconfigures the organisation the account already has, rather
     than leaving a second one behind. */
  const existing = organizations.find((item) => item.id === params.get("org")) ?? organization;
  /* Read once. Re-reading each render would resurrect a draft the user has moved past. */
  const tabDraft = useRef(readDraft()).current;
  const draft = useRef(newestDraft(tabDraft, profile?.onboardingProgress)).current;
  const orgId = existing?.id ?? null;

  const loadRepositories = useCallback(async () => {
    if (!orgId) return;
    try {
      setRepositories(await api.get(`/organizations/${orgId}/repositories`));
    } catch {
      /* A failed poll needs no message: another is four seconds away, and the step
         already reads as "nothing connected yet". */
    }
  }, [orgId]);

  const loadIndexingCounts = useCallback(async () => {
    if (!orgId) return;
    try {
      setIndexingCounts(await api.get(`/organizations/${orgId}/repositories/indexing`));
    } catch {
      /* Same as loadRepositories: a failed poll is not blocking. */
    }
  }, [orgId]);

  const loadStats = useCallback(async () => {
    if (!orgId) return;
    try {
      const result = await api.get(`/organizations/${orgId}/stats/onboarding`);
      setStats(result);
    } catch {
      /* Stats are cosmetic on the final screen. */
    }
  }, [orgId]);

  /* Returning from GitHub carries ?org=, and that is also the first moment repositories
     exist, so this does not wait for the poll. */
  useEffect(() => {
    loadRepositories();
    loadIndexingCounts();
    loadStats();
  }, [loadRepositories, loadIndexingCounts, loadStats, params]);

  /* Coming back from GitHub, the list is still empty: the installation is linked, but its
     repositories are adopted by the worker's next discovery pass, not by the redirect. So
     an empty list after the round trip means "not yet", and is polled like indexing is. */
  const returnedFromGitHub = Boolean(params.get("org"));
  useEffect(() => {
    const items = repositories?.items ?? [];
    const settling = items.some(
      (item) => item.indexStatus === "indexing" || item.indexStatus === "not_indexed",
    );
    const awaitingAdoption = returnedFromGitHub && items.length === 0;
    if (!settling && !awaitingAdoption) return undefined;
    const timer = setInterval(() => {
      loadRepositories();
      loadIndexingCounts();
      loadStats();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [repositories, returnedFromGitHub, loadRepositories, loadIndexingCounts, loadStats]);

  function checkHandle(slug) {
    clearTimeout(handleCheckTimer.current);
    setHandleError("");
    const trimmed = slug.trim();
    if (!trimmed || !/^[a-z0-9][a-z0-9-]{0,62}$/.test(trimmed)) return;
    if (existing?.slug === trimmed) return;
    handleCheckTimer.current = setTimeout(async () => {
      try {
        const result = await api.get(`/onboarding/handle?slug=${encodeURIComponent(trimmed)}`);
        if (!result.available) setHandleError("That handle isn't available.");
      } catch {
        /* A failed check is not blocking: the server will reject at creation time. */
      }
    }, HANDLE_CHECK_MS);
  }

  /* Created here rather than on the way out of the org step. `Gate` reads "has an
     organisation" as "has onboarded", so an organisation created earlier would strand
     anyone who abandoned the wizard on the dashboard, never offered the rest of the
     steps. Here it is created only because the next thing needs one to exist. */
  async function connectGitHub(step, answers) {
    if (actionPending.current) return;
    actionPending.current = true;
    setBusy(true);
    setError("");
    try {
      let id = createdOrg.current?.id ?? orgId;
      if (!id) {
        const created = await api.post("/organizations", {
          name: answers.org.trim(),
          slug: answers.handle.trim(),
        });
        id = created.id;
        createdOrg.current = created;
      }
      try {
        /* Written before leaving, because the browser is about to visit github.com and
           come back in through the API. */
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ step, answers, savedAt: new Date().toISOString() }));
      } catch {
        /* A private window loses the draft and the wizard restarts. Survivable; refusing
           to connect over it would not be. */
      }
      const { url } = await api.get(`/organizations/${id}/github/install-url`);
      window.location.assign(url);
    } catch (failure) {
      setError(Object.values(failure.fields ?? {})[0] || failure.message || "Could not connect to GitHub.");
      setBusy(false);
      actionPending.current = false;
    }
  }

  /* Throws on refusal so the row's Retry button can show why. Re-reading the list moves the
     row to Queued, which also restarts the poll above. */
  async function retryIndex(repositoryId) {
    await api.post(`/organizations/${orgId}/repositories/${repositoryId}/retry-index`);
    announceIndexRequested(repositoryId);
    loadRepositories();
    loadIndexingCounts();
  }

  /* Best effort: a failed save costs at most the steps since the last one that landed,
     and must never stop the wizard. */
  function saveProgress(step, answers) {
    if (saved.current) return;
    progressQueue.current = progressQueue.current
      .then(() => api.post("/users/me/onboarding-progress", { step, answers }))
      .catch(() => {});
  }

  async function finish(answers) {
    if (actionPending.current) return;
    actionPending.current = true;
    setBusy(true);
    setError("");
    try {
      /* Completing clears the saved progress, so a save still in flight must land first
         or it would bring the finished wizard back on the next sign in. */
      await progressQueue.current;
      if (!saved.current) await api.post("/onboarding/complete", {
        orgId: createdOrg.current?.id ?? orgId,
        name: answers.org?.trim() || answers.name?.trim() || user.email,
        handle: answers.handle?.trim() || existing?.slug || "",
        site: answers.site ?? "",
        size: answers.size ?? "",
        why: answers.why ?? [],
        source: answers.source ?? "",
        cfg: answers.cfg,
        /* The invite step collects addresses as free text; blank rows are not people. */
        invites: (answers.invites ?? []).map((entry) => String(entry).trim()).filter(Boolean),
        role: answers.role ?? "Member",
      });
      saved.current = true;
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* Storage must not block navigation. */ }
      const session = await refresh();
      if (!session) throw new Error("Setup was saved, but your session could not be refreshed. Please sign in again.");
      navigate("/analytics", { replace: true });
    } catch (failure) {
      /* Stay on the step. A wizard that advanced past a failed save would be reporting a
         configuration the organisation does not have. */
      setError(Object.values(failure.fields ?? {})[0] || failure.message || "Could not save setup.");
    } finally {
      setBusy(false);
      actionPending.current = false;
    }
  }

  /* GitHub returns every installation here, including "Manage" from Settings after setup
     is done. The wizard writes a tab draft before it leaves for GitHub and Settings does
     not, so a finished account coming back without one came from Settings and belongs
     back there, not at the start of a wizard it already completed. */
  if (returnedFromGitHub && profile?.onboardingCompleted && !tabDraft) {
    return <Navigate to="/settings/providers" replace />;
  }

  return (
    <OnboardingApp
      account={{
        name: profile?.name || "",
        email: user?.email || "",
        org: existing?.name || "",
        handle: existing?.slug || "",
      }}
      initial={draft}
      github={{
        /* Adopted repositories count before their index request is submitted. */
        connected: (repositories?.items?.length ?? 0) > 0 || (indexingCounts?.submitted ?? 0) > 0,
        repositories: repositories?.items ?? [],
        counts: indexingCounts,
        onConnect: connectGitHub,
        onRetry: orgId ? retryIndex : null,
      }}
      stats={stats}
      busy={busy}
      error={error}
      handleError={handleError}
      onCheckHandle={checkHandle}
      onFinish={finish}
      onProgress={saveProgress}
      onOpenDocs={() => window.open(DOCS_URL, "_blank", "noopener,noreferrer")}
      onExit={signOut}
    />
  );
}
